'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { m as motion, LazyMotion, domAnimation } from 'motion/react';
import { ResponsiveHeader } from './ResponsiveHeader';
import { ResponsiveFooter } from './ResponsiveFooter';
import {
  Database,
  Layout,
  Cloud,
  CheckCircle2,
  Server,
  Code2,
  PhoneCall,
  ChevronDown,
} from 'lucide-react';

const faqs = [
  {
    question: 'What is full stack development?',
    answer:
      'Full stack development is the end-to-end engineering of both the client-side (frontend) and server-side (backend) of an application. This includes building the user interface, managing business logic through APIs, and architecting database structures. At Zumify, we handle the entire stack to ensure performance, security, and scalability are baked into every layer of your software.',
  },
  {
    question: 'How long does it take to build a custom full stack application?',
    answer:
      'A typical Minimum Viable Product (MVP) usually takes 8 to 12 weeks to design, develop, and launch. More complex enterprise solutions with intricate integrations or high security requirements can take 4-6 months. Our agile process ensures that we deliver functional increments quickly while maintaining long-term technical debt control.',
  },
  {
    question: 'Do you handle cloud infrastructure and DevOps?',
    answer:
      "Yes. We don't just write code; we ensure it runs reliably. We architect cloud environments on AWS, Google Cloud, and Vercel, implementing automated CI/CD pipelines, containerization (Docker/Kubernetes), and serverless architectures to ensure your app scales automatically with your user base.",
  },
  {
    question: 'What modern database solutions do you implement?',
    answer:
      'We select the database that best fits your data model. We typically use PostgreSQL for robust relational data, MongoDB for flexible document storage, and Redis for high-performance caching. All our database architectures prioritize data integrity, encryption at rest, and optimized query performance.',
  },
  {
    question: 'Can you take over or rescue an existing project?',
    answer:
      "Absolutely. We specialize in 'Project Rescue'—auditing existing codebases to identify security vulnerabilities, performance bottlenecks, and technical debt. We provide a clear roadmap to refactor, stabilize, and eventually scale your existing application to modern standards.",
  },
  {
    question: 'What are your pricing ranges for a full stack application?',
    answer:
      'Our full stack application pricing varies based on factors such as functionality, design complexity, and technical requirements. To provide an accurate and tailored estimate, we encourage you to connect with our sales team to discuss your project in detail.',
  },
];

function FAQItem({
  question,
  answer,
  isOpen,
  onClick,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) {
  return (
    <div className="border-b border-white/10">
      <button
        className="w-full py-6 flex items-center justify-between text-left hover:text-purple-400 transition-colors group"
        onClick={onClick}
      >
        <span className="text-xl sm:text-2xl font-semibold text-white group-hover:text-purple-300 transition-colors">
          {question}
        </span>
        <ChevronDown
          className={`w-6 h-6 text-purple-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="overflow-hidden"
      >
        <p className="pb-6 text-stone-400 text-lg leading-relaxed">{answer}</p>
      </motion.div>
    </div>
  );
}

export function ResponsiveFullStack() {
  const router = useRouter();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0 },
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0 },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const pulseSlow = {
    animate: {
      opacity: [0.3, 0.5, 0.3],
      scale: [1, 1.1, 1],
      transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' as const },
    },
  };

  return (
    <LazyMotion features={domAnimation}>
      <div className="min-h-screen bg-zinc-900 flex flex-col relative overflow-hidden">
        <ResponsiveHeader />

        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center pt-32 lg:pt-56 pb-20">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-zinc-900/80 sm:bg-zinc-900/70 z-10" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-zinc-900 to-transparent z-10" />
            {/* Using an existing web design cover image or similar solid dark placeholder to maintain style */}
            <img
              src="/images/responsive-hero.webp"
              alt="Full Stack Development"
              className="w-full h-full object-cover object-center sm:object-top opacity-50 filter hue-rotate-180 saturate-150"
            />
          </div>

          <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            <motion.div
              className="absolute top-20 left-10 w-72 h-72 bg-blue-600/30 rounded-full blur-[100px]"
              variants={pulseSlow}
              animate="animate"
            />
            <motion.div
              className="absolute bottom-20 right-10 w-96 h-96 bg-purple-600/30 rounded-full blur-[100px]"
              animate={{
                opacity: [0.2, 0.4, 0.2],
                scale: [1, 1.2, 1],
                transition: { duration: 6, repeat: Infinity, ease: 'easeInOut' },
              }}
            />
          </div>

          <div className="relative w-full px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center z-10">
            <div className="max-w-4xl mx-auto backdrop-blur-sm sm:backdrop-blur-none bg-zinc-900/40 sm:bg-transparent p-6 rounded-3xl">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="space-y-8"
              >
                <motion.h1
                  variants={fadeInUp}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-[1.1] drop-shadow-2xl"
                >
                  Full Stack <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-500 drop-shadow-md">
                    Application Development
                  </span>
                </motion.h1>

                <motion.p
                  variants={fadeInUp}
                  className="text-lg sm:text-lg md:text-xl text-stone-200 lg:text-stone-300 max-w-2xl mx-auto leading-relaxed drop-shadow-xl font-medium"
                >
                  Scale your business with high-performance, full-stack applications engineered for
                  impact. At Zumify, we bridge the gap between complex business requirements and
                  elegant technical solutions. From responsive frontends to resilient cloud
                  architectures, our Hawaii-based team delivers custom software that doesn't just
                  work—it grows with you.
                </motion.p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="px-4 sm:px-6 lg:px-8 pb-20 pt-10 lg:pt-20 relative border-t border-white/5">
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-16 lg:mb-20"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={fadeInUp}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                End-to-End Solutions
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full" />
              <p className="mt-6 text-lg text-stone-300 max-w-2xl mx-auto">
                We handle the entire technical lifecycle of your product. By integrating frontend
                precision with backend stability, we build software that is secure, maintainable,
                and optimized for high-traffic environments.
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto space-y-12 lg:space-y-16 mt-16">
              {/* Frontend */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                className="bg-zinc-900/40 p-8 lg:p-10 rounded-3xl border border-white/5 backdrop-blur-sm relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0 w-1 p-0 h-full bg-gradient-to-b from-blue-400 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="flex items-center gap-4 mb-4 pl-4">
                  <Layout className="w-8 h-8 text-blue-400" />
                  <h3 className="text-2xl lg:text-3xl font-bold text-white">
                    Client Facing Frontend
                  </h3>
                </div>
                <p className="text-lg text-stone-300 leading-relaxed pl-4">
                  We build highly interactive, accessible, and fast user interfaces using modern
                  frameworks like React and Next.js. Our focus is on creating seamless, state-driven
                  experiences that feel instantaneous, ensuring your users stay engaged across all
                  device types.
                </p>
              </motion.div>

              {/* Database */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInLeft}
                className="bg-zinc-900/40 p-8 lg:p-10 rounded-3xl border border-white/5 backdrop-blur-sm relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0 w-1 pt-8 h-full bg-gradient-to-b from-indigo-400 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="flex items-center gap-4 mb-4 pl-4">
                  <Database className="w-8 h-8 text-indigo-400" />
                  <h3 className="text-2xl lg:text-3xl font-bold text-white">Database Management</h3>
                </div>
                <p className="text-lg text-stone-300 leading-relaxed pl-4">
                  Data integrity and speed are our priorities. We design scalable schemas using
                  PostgreSQL and MongoDB, optimized for complex queries and real-time
                  synchronization. Our architectures ensure your data remains secure, accessible,
                  and ready for high-volume growth.
                </p>
              </motion.div>

              {/* Cloud Infrastructure */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInRight}
                className="bg-zinc-900/40 p-8 lg:p-10 rounded-3xl border border-white/5 backdrop-blur-sm relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0 w-1 pt-8 h-full bg-gradient-to-b from-purple-400 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="flex items-center gap-4 mb-4 pl-4">
                  <Cloud className="w-8 h-8 text-purple-400" />
                  <h3 className="text-2xl lg:text-3xl font-bold text-white">
                    Cloud Infrastructure
                  </h3>
                </div>
                <p className="text-lg text-stone-300 leading-relaxed pl-4">
                  We deploy your applications to global-scale infrastructure using AWS, Vercel, and
                  Google Cloud. With automated CI/CD pipelines, 99.9% uptime targets, and proactive
                  monitoring, we ensure your software is always available and performing at its
                  peak.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Why Choose Zumify */}
        <section className="px-4 sm:px-6 lg:px-8 py-20 lg:py-32 relative overflow-hidden bg-zinc-800/20 border-y border-white/5">
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <motion.div
                className="w-full lg:w-1/2 space-y-8"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInLeft}
              >
                <div className="space-y-4">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                    Your Local Software <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                      Agency in Hawaii
                    </span>
                  </h2>
                  <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" />
                </div>

                <p className="text-lg text-stone-300 leading-relaxed">
                  Based in Honolulu, Zumify combines deep technical expertise with local market
                  insights. We don't just build apps; we architect digital foundations that allow
                  local businesses to compete and win on a global scale.
                </p>

                <ul className="space-y-4">
                  {[
                    'Next-generation tech stacks (React, Node, Postgres)',
                    'Secure Authentication and Data Privacy',
                    'Seamless API and 3rd-party integrations',
                    'High availability and automated backups',
                  ].map((item, idx) => (
                    <motion.li
                      key={idx}
                      className="flex items-center gap-3 text-stone-200"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + idx * 0.1 }}
                    >
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/30">
                        <CheckCircle2 className="w-4 h-4 text-blue-400" />
                      </div>
                      <span className="text-lg">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                className="w-full lg:w-1/2"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInRight}
              >
                <div className="relative aspect-square sm:aspect-video lg:aspect-square rounded-3xl bg-zinc-900 border border-white/10 shadow-2xl shadow-blue-500/20 overflow-hidden group flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 pointer-events-none" />
                  {/* Abstract Backend/Frontend visualization */}
                  <Server className="w-40 h-40 text-blue-500/30 absolute group-hover:scale-110 transition-transform duration-700" />
                  <Code2 className="w-20 h-20 text-indigo-400 absolute z-10 drop-shadow-[0_0_15px_rgba(99,102,241,0.5)]" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-20 lg:py-32 overflow-hidden relative border-b border-white/5">
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-16 lg:mb-24"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={fadeInUp}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Our Full Stack{' '}
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Process
                </span>
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full" />
            </motion.div>

            <div className="space-y-24 lg:space-y-40">
              {[
                {
                  step: '01',
                  icon: <PhoneCall className="w-10 h-10 text-blue-400" />,
                  title: 'Discovery & Architecture',
                  description:
                    'We start by deep-diving into your business logic and user requirements. We then architect the database schemas, design the API contracts, and map out the cloud infrastructure to ensure a solid foundation before the first line of code is written.',
                  bgColor: 'from-blue-500 to-transparent',
                  borderColor: 'border-blue-500/30',
                  shadow: 'shadow-[0_0_30px_rgba(59,130,246,0.3)]',
                  image: '/images/intro-meeting.webp',
                },
                {
                  step: '02',
                  icon: <Layout className="w-10 h-10 text-indigo-400" />,
                  title: 'UI/UX & Frontend Build',
                  description:
                    'We build pixel-perfect, responsive frontends using Next.js and Tailwind CSS. Our development focus is on high-performance state management and intuitive interactions that make complex data feel simple for your users.',
                  bgColor: 'from-indigo-500 to-transparent',
                  borderColor: 'border-indigo-500/30',
                  shadow: 'shadow-[0_0_30px_rgba(99,102,241,0.3)]',
                  image: '/images/figma-step.webp',
                },
                {
                  step: '03',
                  icon: <Database className="w-10 h-10 text-purple-400" />,
                  title: 'Backend & Database',
                  description:
                    'Our backend engineering team builds secure, scalable REST or GraphQL APIs. We implement rigorous authentication (JWT/OAuth), data validation, and payment integrations, ensuring your core business logic is bulletproof.',
                  bgColor: 'from-purple-500 to-transparent',
                  borderColor: 'border-purple-500/30',
                  shadow: 'shadow-[0_0_30px_rgba(168,85,247,0.3)]',
                  image: '/images/development.webp',
                },
                {
                  step: '04',
                  icon: <Cloud className="w-10 h-10 text-pink-400" />,
                  title: 'Cloud Deployment',
                  description:
                    'We launch your application into a production-hardened cloud environment. We set up automated testing, performance monitoring, and SSL security, ensuring your app is fast, secure, and ready for thousands of concurrent users.',
                  bgColor: 'from-pink-500 to-transparent',
                  borderColor: 'border-pink-500/30',
                  shadow: 'shadow-[0_0_30px_rgba(236,72,153,0.3)]',
                  image: '/images/cloud-infrastructure.webp',
                },
              ].map((step, idx) => {
                const isEven = idx % 2 === 1;

                return (
                  <div
                    key={idx}
                    className="relative z-10 flex flex-col lg:flex-row items-center gap-12 lg:gap-20"
                  >
                    <motion.div
                      className={`w-full lg:w-1/2 space-y-6 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, amount: 0.3 }}
                      variants={isEven ? fadeInRight : fadeInLeft}
                    >
                      <div className="flex items-center gap-6 mb-2">
                        <span
                          className={`text-6xl lg:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-br ${step.bgColor} opacity-50`}
                        >
                          {step.step}
                        </span>
                        <div
                          className={`p-4 rounded-2xl bg-zinc-800 border ${step.borderColor} shadow-lg`}
                        >
                          {step.icon}
                        </div>
                      </div>

                      <h3 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
                        {step.title}
                      </h3>

                      <p className="text-lg text-stone-300 leading-relaxed max-w-xl">
                        {step.description}
                      </p>
                    </motion.div>

                    <motion.div
                      className={`w-full lg:w-1/2 ${isEven ? 'lg:order-1' : 'lg:order-2'}`}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, amount: 0.3 }}
                      variants={isEven ? fadeInLeft : fadeInRight}
                    >
                      <div
                        className={`relative w-full aspect-[4/3] rounded-[24px] bg-zinc-900 border-2 ${step.borderColor} ${step.shadow} overflow-hidden group`}
                      >
                        {'image' in step && step.image ? (
                          <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900 overflow-hidden flex items-center justify-center p-6">
                            <img
                              src={step.image}
                              alt={step.title}
                              className="w-full h-full object-contain rounded-xl group-hover:scale-105 transition-transform duration-700 relative z-10 shadow-xl shadow-black/50"
                            />
                          </div>
                        ) : (
                          <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center p-8">
                            <div
                              className={`w-full h-full border border-white/5 rounded-2xl bg-gradient-to-br ${step.bgColor} backdrop-blur-3xl flex items-center justify-center group-hover:scale-105 transition-transform duration-700`}
                            >
                              <div className="w-32 h-32 rounded-full bg-white/5 backdrop-blur-md flex items-center justify-center border border-white/10">
                                {step.icon}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </div>
                    </motion.div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-24 bg-zinc-900/50 relative z-20">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Full Stack FAQs
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full" />
            </motion.div>

            <div className="space-y-2">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Bottom Section */}
        <motion.section
          className="px-4 sm:px-6 lg:px-8 py-20 mb-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          variants={fadeInUp}
        >
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-blue-500/10 via-indigo-500/10 to-purple-500/10 backdrop-blur-xl border border-white/10 rounded-3xl p-8 lg:p-12 shadow-[0_0_50px_rgba(99,102,241,0.15)]">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Full Stack Pricing
            </h2>
            <p className="text-lg sm:text-xl text-stone-300 mb-8 max-w-2xl mx-auto">
              Partner with the leading software agency Hawaii to build a custom full stack application. All custom full stack projects are customly quoted based on functionality, design complexity, and technical requirements.
              Contact our team for a free consultation and we'll be happy to provide you with a custom quote.
            </p>
            <button
              onClick={() => router.push('/contact')}
              className="px-10 py-4 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 text-white rounded-full font-bold text-lg hover:scale-105 transition-transform duration-300 shadow-lg shadow-indigo-500/30"
            >
              Get a Free Consultation
            </button>
          </div>
        </motion.section>

        <ResponsiveFooter />
      </div>
    </LazyMotion>
  );
}
